import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import clsx from 'clsx'
import { ThemeMode } from '@hotmart/space-lib/dist/styles/themes/enum'
import { useUser } from 'src/hook/useUser'
import { MentionListItem } from '../mentionListItem'
import { RequestState } from '../../hooks/useMentionUtils'
import { Wrapper } from './mentionList.styles'
import { MentionItem, MentionListProps } from './mentionList.types'

const MentionList = forwardRef(
  ({ items, query, command, onMentionClick }: MentionListProps, ref) => {
    const [selectedIndex, setSelectedIndex] = useState(0)
    const { theme } = useUser()
    const wrapperRef = useRef<HTMLDivElement>(null)

    const isValidMentionList = Array.isArray(items) && items.length > 0
    const isEmptyState = isValidMentionList && items[0].id === 0

    const selectItem = (index: number) => {
      const item = items[index]
      if (item) command(item)
    }

    const changeScrollPosition = (index: number) => {
      if (wrapperRef?.current) {
        const mentionItem = wrapperRef.current.querySelector('button')
        const mentionItemHeight = mentionItem?.clientHeight || 0
        wrapperRef.current.scrollTop = index * mentionItemHeight
      }
    }

    const upHandler = () => {
      setSelectedIndex((prevIndex) => {
        const newIndex = (prevIndex + items.length - 1) % items.length
        changeScrollPosition(newIndex)
        return newIndex
      })
    }

    const downHandler = () => {
      setSelectedIndex((prevIndex) => {
        const newIndex = (prevIndex + 1) % items.length
        changeScrollPosition(newIndex)
        return newIndex
      })
    }

    const enterHandler = () => {
      selectItem(selectedIndex)
    }

    useEffect(() => {
      setSelectedIndex(0)
    }, [items])

    const handleClick = ({ item, index }) => {
      selectItem(index)
      onMentionClick?.({ ...item, query })
    }

    useImperativeHandle(ref, () => ({
      onKeyDown: ({ event }: { event: KeyboardEvent }) => {
        if (event.key === 'ArrowUp') {
          upHandler()
          return true
        }

        if (event.key === 'ArrowDown') {
          downHandler()
          return true
        }

        if (event.key === 'Enter' || event.key === 'Tab') {
          if (!isEmptyState) {
            enterHandler()
          }

          return true
        }

        return false
      }
    }))

    return (
      <Wrapper
        ref={wrapperRef}
        className={clsx(
          '_border _border-gray-200 _flex-column  _shadow-lg _rounded-lg _overflow-y-auto _position-relative',
          {
            '_bg-white': theme !== ThemeMode.DARK,
            '_bg-black': theme === ThemeMode.DARK,
            '_d-flex': isValidMentionList,
            '_d-none': !isValidMentionList
          }
        )}
      >
        {isValidMentionList &&
          items.map((item: MentionItem, index: number) => (
            <MentionListItem
              key={item.id}
              {...item}
              noResults={item.label === RequestState.NO_RESULTS}
              isSelected={index === selectedIndex}
              onClick={() => handleClick({ item, index })}
            />
          ))}
      </Wrapper>
    )
  }
)

MentionList.displayName = 'MentionList'

export default MentionList
